import { useTranslation } from "@sagot/i18n";
import { useFormatters } from "../shared";
import { UsageMeter } from "./usage-meter";

interface Figure {
  used: number;
  limit: number | null;
}

/** The figures of `GET /v1/service-usage` that arrived after tickets and storage (SD-X-BIL-01). */
export interface MoreServiceUsage {
  connectors: Figure;
  catalogue_items: Figure;
  orders: Figure;
  assets: Figure;
  field_seats: Figure;
  staff_ai_credits: Figure;
}

type Key = "connectors" | "catalogue" | "orders" | "assets" | "fieldSeats" | "staffAi";

/**
 * Which meters a workspace sees: connectors, orders and staff AI always (what the module bills on), the catalogue, the
 * equipment and the field seats only once the plan allows them or they are in use - a Standard workspace has no use for
 * a catalogue meter stuck at 0 of 0.
 */
export function visibleMeters(data: MoreServiceUsage): Key[] {
  const used = (figure: Figure) => figure.used > 0 || (figure.limit ?? 1) > 0;
  const keys: Key[] = ["connectors"];
  if (used(data.catalogue_items)) keys.push("catalogue");
  if (used(data.catalogue_items) || data.orders.used > 0) keys.push("orders");
  if (data.assets.used > 0 || used(data.field_seats)) keys.push("assets");
  if (used(data.field_seats)) keys.push("fieldSeats");
  keys.push("staffAi");
  return keys;
}

export function ServiceUsageMore({ data }: { data: MoreServiceUsage }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const figures: Record<Key, Figure> = {
    connectors: data.connectors,
    catalogue: data.catalogue_items,
    orders: data.orders,
    assets: data.assets,
    fieldSeats: data.field_seats,
    staffAi: data.staff_ai_credits,
  };
  return (
    <>
      {visibleMeters(data).map((key) => {
        const figure = figures[key];
        return (
          <UsageMeter
            key={key}
            label={t(`service.cards.billing.${key}`)}
            used={figure.used}
            limit={figure.limit}
            usedLabel={fmt.number(figure.used)}
            limitLabel={figure.limit === null ? t("service.cards.billing.noLimit") : t("service.cards.billing.of", { value: fmt.number(figure.limit) })}
            hint={t(`service.cards.billing.${key}Hint`)}
          />
        );
      })}
    </>
  );
}
